import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { MultiSelectorResultContainer } from './multi-selector-result-container';
import { findItemIndex } from './util';
import { getWithResultContainerStyles } from './styles';

/**
 * HOC: add result container for the any selector
 */

export const withResultContainer = WrappedComponent =>
  withDefaultStyles(class extends React.Component {
    static propTypes = {
      /**
       * JSS injected classes
       */
      classes: PropTypes.objectOf(PropTypes.any).isRequired,
      /**
       * i18n for localization
       */
      i18n: PropTypes.shape({
        getString: PropTypes.func.isRequired,
        parseDecimal: PropTypes.func.isRequired,
      }).isRequired,

      /**
       * options for result container
       */
      resultContainerOptions: PropTypes.shape({
        /**
          * customized header style for result container, when omitted, will use the default style
          */
        renderHeader: PropTypes.func,
        /**
          * customized header style for result container, required
          */
        renderItem: PropTypes.func.isRequired,
        /**
         * customized empty list style for result container,
         * when omitted, will use the default style
         */
        renderEmptyList: PropTypes.func,
        /**
         * page size, required
         */
        pageSize: PropTypes.number,
        /**
          * callback for the Done btn clicked, with the selected items, required
          */
        onSelectDone: PropTypes.func.isRequired,
        /**
        * callback for the Cancel btn clicked, with the selected items, required
        */
        onSelectCancel: PropTypes.func.isRequired,
        /**
        * hide the save and cancel btn
        */
        isHideSaveBar: PropTypes.bool,
        /**
        * Call back when result list changed
        */
        onResultChanged: PropTypes.func,
        /**
        * callback function that is called select action is performed
        */
        onRemove: PropTypes.func,
        /**
        * callback function that is called select action is performed
        */
        onRemoveAll: PropTypes.func,
      }).isRequired,
    };

    constructor(props) {
      super(props);
      this.state = {
        resultList: [],
      };
    }

    /**
     * add the items from the selector to the resultlist
     * @param {Object} selectedItems - items from selector
     * @returns {undefined} update the resultlist
     */
    onSelectorItemsClick = (selectedItems) => {
      this.updateResultList(selectedItems, false);
    };

    /**
    * remove the items from the result container to the resultlist
    * @param {Object} removeItems - items from result container
    * @returns {undefined} update the resultlist
    */
    onResultItemClick = (removeItems) => {
      this.updateResultList(removeItems, true);
    };

    /**
    * update the resultlist
    * @param {Object} items - items should be updated
    * @param {boolean} isRemove - add or remove
    * @returns {undefined} update the state: resultList
    */
    updateResultList = (items, isRemove) => {
      if (!items) {
        return;
      }

      let newResult;
      if (items.length === 0 && isRemove) {
        newResult = [];
      } else {
        newResult = [...this.state.resultList];
        for (let i = 0; i < items.length; i += 1) {
          const index = findItemIndex(items[i], this.state.resultList);
          if (isRemove) {
            if (index > -1) {
              newResult.splice(index, 1);
            }
          } else if (index === -1) {
            const newSelectedItem = { ...items[i] };
            newSelectedItem.isShowRemoveArrow = true;
            newResult.push(newSelectedItem);
          }
        }
      }
      this.setState({
        resultList: newResult,
      }, () => {
        const { isHideSaveBar, onSelectDone, onResultChanged } = this.props.resultContainerOptions;
        if (isHideSaveBar) {
          onSelectDone(this.state.resultList);
        }
        if (onResultChanged) {
          onResultChanged(newResult);
        }
      });
    };

    render() {
      const { resultList } = this.state;
      const { classes, i18n } = this.props;
      const { onSelectDone, onSelectCancel, isHideSaveBar } = this.props.resultContainerOptions;

      return (
        <div className={classNames('multi-selector', classes.withResultContainer)}>
          <div className={classNames('multi-selector-content', classes.withResultContent)}>
            <WrappedComponent
              {...this.props}
              resultContainerOptions={
                {
                  ...this.props.resultContainerOptions,
                  updateResultList: this.onSelectorItemsClick,
                  resultList,
                }
              }
            />
            <MultiSelectorResultContainer
              {...this.props}
              resultContainerOptions={
                {
                  ...this.props.resultContainerOptions,
                  updateResultList: this.onResultItemClick,
                  resultList,
                }
              }
            />
          </div>
          {
            isHideSaveBar ?
              null :
              <div className={classNames('save-bar', classes.saveBar)}>
                <input type="button" value={i18n.getString('Done')} className="btn-done btn btn-primary" onClick={() => { onSelectDone(resultList); }} />
                <input type="button" value={i18n.getString('Cancel')} className="btn-cancel btn btn-default" onClick={onSelectCancel} />
              </div>
          }
        </div>
      );
    }
  }, getWithResultContainerStyles);
